import React, { useState, useEffect } from "react";
import apiClient from "../api/apiClient";
import axios from "axios"; // Import axios directly
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError(null);

      // Add timeout using axios directly
      const source = axios.CancelToken.source();
      const timeoutId = setTimeout(
        () => source.cancel("Request timeout"),
        10000,
      );

      const response = await apiClient.get(`/services`, {
        cancelToken: source.token,
      });

      clearTimeout(timeoutId);

      // Validate response structure
      if (Array.isArray(response.data)) {
        setServices(response.data);
      } else {
        throw new Error("Invalid data format received from server");
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        // Use axios.isCancel directly
        setError(new Error("Request timeout: Please check your connection"));
      } else if (err.response) {
        setError(
          new Error(
            `Server Error: ${err.response.status} - ${
              err.response.data?.message || "Unknown error"
            }`,
          ),
        );
      } else if (err.request) {
        setError(
          new Error(
            "Network Error: Unable to reach the server. Please check your connection.",
          ),
        );
      } else {
        setError(new Error(`Error: ${err.message}`));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, [retryCount]);

  const handleRetry = () => {
    setRetryCount((prev) => prev + 1);
  };

  // Show full page loading
  if (loading && retryCount === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <div className="text-center">
          <div
            className="spinner-border text-primary"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          >
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-3">Chargement des services...</p>
        </div>
      </div>
    );
  }

  // Show full page error if no services data
  if (error && services.length === 0) {
    return (
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-6">
            <div className="alert alert-danger mt-5" role="alert">
              <div className="text-center">
                <i className="fas fa-exclamation-triangle fa-3x mb-3 text-danger"></i>
                <h4>Erreur de chargement des services</h4>
                <p className="mb-3">{error.message}</p>
                <button
                  className="btn btn-primary"
                  onClick={handleRetry}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></span>
                      Chargement...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-redo me-2"></i>
                      Réessayer
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // JSON-LD structured data for services
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Services - DVELGE GROUP",
    url: "https://www.dvelgegroup.com/services",
    serviceType: "Digital & IT Solutions",
    provider: {
      "@type": "Organization",
      name: "DVELGE GROUP",
      url: "https://dvelgegroup.com",
      logo: "https://dvelgegroup.com/images/logo.png",
      contactPoint: {
        "@type": "ContactPoint",
        telephone: "+224627020871",
        contactType: "Customer Service",
      },
    },
  };

  return (
    <div>
      <Helmet>
        <title>Nos Services | DVELGE GROUP</title>
        <meta
          name="description"
          content="Découvrez les services de DVELGE GROUP : des solutions digitales et informatiques fiables et adaptées à vos besoins."
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Nos Services | DVELGE GROUP" />
        <meta
          property="og:description"
          content="Découvrez les services de DVELGE GROUP : des solutions digitales et informatiques fiables et adaptées à vos besoins."
        />
        <meta
          property="og:url"
          content="https://www.dvelgegroup.com/services"
        />
        <meta
          property="og:image"
          content="https://dvelgegroup.com/images/logo.png"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Nos Services | DVELGE GROUP" />
        <meta
          name="twitter:description"
          content="Découvrez les services de DVELGE GROUP : des solutions digitales et informatiques fiables et adaptées à vos besoins."
        />
        <meta
          name="twitter:image"
          content="https://dvelgegroup.com/images/logo.png"
        />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Page Heading */}
      <section className="page-heading-sec serv-page-head r-bg-a pt85 pb120">
        <div className="container">
          <div className="row pt80 justify-content-center">
            <div className="col-lg-9">
              <div className="page-headings service-page">
                <span
                  className="sub-heading mb15"
                  data-aos="fade-in"
                  data-aos-delay="200"
                >
                  Nos Services
                </span>
                <h1 data-aos="fade-in" data-aos-delay="400">
                  <span className="ree-text rt45">DVELGE GROUP</span>,
                  L'innovation au service de votre confort
                </h1>

                {/* Show warning if there was an error but we have cached data */}
                {error && services.length > 0 && (
                  <div className="alert alert-warning mt-3" role="alert">
                    <small>
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      Données en cache: {error.message}
                      <button
                        className="btn btn-sm btn-outline-primary ms-2"
                        onClick={handleRetry}
                        disabled={loading}
                      >
                        {loading ? "Chargement..." : "Actualiser"}
                      </button>
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="r-bg-x sec-pad">
        <div className="container">
          <div className="row">
            <div className="col-lg-4">
              <div className="page-headings m-center">
                <h3>
                  Nous vous proposons des solutions accessibles, fiables et
                  adaptées à vos besoins.
                </h3>
                <Link to="/contacts" className="ree-btn ree-btn-grdt2 mt40">
                  Besoin d'un Service{" "}
                  <i className="fas fa-arrow-right fa-btn"></i>
                </Link>
              </div>
            </div>

            <div className="col-lg-8">
              <div className="ml25 m-ml0 m-mt60">
                <h2 className="h1a m-center">Nos Services</h2>

                {/* Loading state for services section during retry */}
                {loading && retryCount > 0 ? (
                  <div className="d-flex justify-content-center align-items-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Chargement...</span>
                    </div>
                    <span className="ms-3">Mise à jour des services...</span>
                  </div>
                ) : (
                  <div className="services-blocks-set mt30">
                    {services.length === 0 && !loading ? (
                      <div className="text-center py-5">
                        <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                        <p className="text-muted">
                          Aucun service disponible pour le moment.
                        </p>
                        <button
                          className="btn btn-primary"
                          onClick={handleRetry}
                        >
                          <i className="fas fa-redo me-2"></i>
                          Réessayer
                        </button>
                      </div>
                    ) : (
                      services.map((service) => (
                        <div
                          key={service.id_service}
                          className="servic-block-a"
                        >
                          <img
                            src={`${service.image_path}`}
                            alt={service.titre}
                            className="icon70 mb20"
                            onError={(e) => {
                              e.target.src = "/images/placeholder-service.jpg";
                              e.target.alt = "Image non disponible";
                            }}
                          />
                          <Link
                            to={`${service.url}`}
                            className="link-serv"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <i className="fas fa-arrow-right"></i>
                          </Link>
                          <h4 className="mb15">
                            <Link to={`${service.url}`}>{service.titre}</Link>
                          </h4>
                          <p>{service.description}</p>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Served - This section doesn't depend on API data, so it always shows */}
      <section className="r-bg-x sec-pad">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="page-headings text-center">
                <h2 className="mb15">Industries que nous servons</h2>
                <p>
                  Notre processus de conception suit une approche éprouvée. Nous
                  commençons par une compréhension approfondie de vos besoins et
                  créons un plan adapté.
                </p>
              </div>
            </div>
          </div>

          <div className="row mt30">
            {[
              { icon: "finance.png", name: "Finance" },
              { icon: "hotellerie.png", name: "Hôtellerie" },
              { icon: "education.png", name: "Éducation" },
              { icon: "vente-en-detail.png", name: "Vente en détail" },
              { icon: "sante.png", name: "Santé" },
              { icon: "agriculture.png", name: "Agriculture" },
              { icon: "logistique.png", name: "Logistique" },
              { icon: "immobilier.png", name: "Immobilier" },
              { icon: "e-commerce.png", name: "E-commerce" },
              { icon: "divertissement.png", name: "Divertissement" },
              {
                icon: "organisations-a-but-non-lucrative.png",
                name: "Organisations à but non lucratif",
              },
              { icon: "energie.png", name: "Énergie" },
              { icon: "commerce.png", name: "Commerce" },
              {
                icon: "gouvernement-et-services-publics.png",
                name: "Gouvernement et Services Publics",
              },
              { icon: "sports-et-fitness.png", name: "Sports et Fitness" },
            ].map((industry, index) => (
              <div key={index} className="col-lg-3 col-sm-6 col-6">
                <div className="industry-workfor">
                  <img
                    src={`images/icons/${industry.icon}`}
                    alt={industry.name}
                    onError={(e) => {
                      e.target.src = "/images/placeholder-icon.svg";
                      e.target.alt = "Icône non disponible";
                    }}
                  />
                  <h6>{industry.name}</h6>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA - This section doesn't depend on API data, so it always shows */}
      <section className="sec-pad r-bg-a">
        <div className="container">
          <div className="row">
            <div className="col-lg-6 vcenter">
              <div className="cta-heading">
                <span className="sub-heading mb15">Contactez-nous</span>
                <h2>
                  Vous avez un <span className="ree-text rt40">projet</span> en
                  tête ? Parlons-en.
                </h2>
                <Link
                  to="tel:+224627020871"
                  className="ree-btn ree-btn-grdt1 mt40 mr20"
                  target="_blank"
                >
                  <i className="fas fa-phone-alt fb-bigon"></i> Contact
                </Link>
                <Link
                  to="https://wa.me/+224627020871"
                  className="ree-btn whatsapp-bg mt40"
                  target="_blank"
                >
                  <i className="fab fa-whatsapp fb-bigon"></i> WhatsApp
                </Link>
              </div>
            </div>

            <div className="col-lg-6 vcenter text-center">
              <img
                src="images/cta/idea.jpg"
                alt="support client"
                className="img-fluid"
                onError={(e) => {
                  e.target.src = "/images/placeholder-cta.svg";
                  e.target.alt = "Image de support non disponible";
                }}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
